import { createClient, type User } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'
import { appOrigin } from './appUrl'
import i18n from './i18n'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file.'
  )
}

// Remember me: delegates to localStorage (persistent) or sessionStorage (tab-only)
const REMEMBER_KEY = 'betterclose-remember-me'

const authStorage = {
  getItem: (key: string) => {
    return localStorage.getItem(key) || sessionStorage.getItem(key)
  },
  setItem: (key: string, value: string) => {
    const remember = localStorage.getItem(REMEMBER_KEY) !== 'false'
    if (remember) {
      localStorage.setItem(key, value)
      sessionStorage.removeItem(key)
    } else {
      sessionStorage.setItem(key, value)
      localStorage.removeItem(key)
    }
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key)
    sessionStorage.removeItem(key)
  },
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: authStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lock: async (_name: string, _acquireTimeout: number, fn: () => Promise<any>) => {
      return await fn()
    },
  },
})

// Auth helpers
export const authHelpers = {
  // Sign up with email and password
  async signUp(email: string, password: string, firstName: string, phone?: string) {
    // Persist the user's UI locale (FR / EN) into auth.users.user_metadata
    // so the send-email Auth hook (supabase/functions/send-email) can route
    // every subsequent system mail (signup confirmation, password reset,
    // magic link, etc.) to the right localized template. Falls back to 'fr'
    // when i18next hasn't initialized yet (server / first paint edge cases).
    const rawLocale = (i18n.language || 'fr').toLowerCase().split('-')[0]
    const locale: 'fr' | 'en' = rawLocale === 'en' ? 'en' : 'fr'
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          phone: phone || null,
          locale,
        },
        // Use the deployment-canonical origin (VITE_APP_URL first, then
        // window.location.origin). Keeps the email link valid across
        // devices + avoids the Supabase site_url localhost fallback.
        emailRedirectTo: `${appOrigin()}/email-confirmed`,
      },
    })
    return { data, error }
  },

  // Sign in with email and password
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Get current user
  async getCurrentUser(): Promise<User | null> {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.user ?? null
  },

  // Get current session
  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  // Check if user is admin
  async isAdmin(): Promise<boolean> {
    const user = await this.getCurrentUser()
    if (!user) return false

    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .single<{ role: 'user' | 'admin' }>()

    if (error || !data) return false
    return data.role === 'admin'
  },

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user ?? null)
    })
  },
}
